import { useEffect, useRef, useState } from 'react';
import { CircleCheck, CircleX, Loader2 } from 'lucide-react';
import { Surface, Typography } from '@kustom-checkout/morpheus';

import { readTestSession, runTest } from '~/api';
import type { TestResult, TestStatus } from '~/api/types';
import type { ShipwreckConfiguration } from '~/lib/queryState';

const POLL_INTERVAL = 500;

function TestReport({ test }: { test: TestResult }) {
  return (
    <Surface color="weak" className="flex flex-col gap-1">
      <Typography
        variant="p"
        color={test.success ? 'strong' : 'error'}
        className="flex items-center gap-2"
      >
        {test.success ? (
          <CircleCheck size={16} className="shrink-0" aria-hidden />
        ) : (
          <CircleX size={16} className="shrink-0" aria-hidden />
        )}
        {test.name}
      </Typography>
      <Typography variant="small" color="muted" isMultiline>
        {test.description}
      </Typography>
      {!test.success && (
        <Typography variant="small" color="muted" isMultiline>
          <strong>Reason:</strong> {test.output.message}
        </Typography>
      )}
      <Typography variant="small" color="muted" isMultiline>
        <strong>Session ID:</strong> {test.output.service_info.session_id}
      </Typography>
    </Surface>
  );
}

interface TestRunnerProps {
  configuration: ShipwreckConfiguration;
  recaptchaAnswer: string | null;
  onComplete: (kssSessionId: string, kssVersion: string) => void;
  onError: (status?: number) => void;
}

export function TestRunner({
  configuration,
  recaptchaAnswer,
  onComplete,
  onError,
}: TestRunnerProps) {
  const [testStatus, setTestStatus] = useState<TestStatus | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // The callbacks are recreated on every App render; a ref keeps the polling
  // loop from restarting and firing a second test run.
  const callbacks = useRef({ onComplete, onError });
  callbacks.current = { onComplete, onError };

  useEffect(() => {
    let cancelled = false;

    const poll = async (testSession: string) => {
      const response = await readTestSession(testSession);

      if (cancelled) return;

      setTestStatus(response.data);

      if (response.status === 200 && response.data.running) {
        timeoutRef.current = setTimeout(() => void poll(testSession), POLL_INTERVAL);
        return;
      }

      const serviceInfo = response.data.tests?.[0]?.output?.service_info;
      callbacks.current.onComplete(
        serviceInfo?.session_id ?? '',
        serviceInfo?.service_version ?? '',
      );
    };

    const start = async () => {
      try {
        const { headers } = await runTest({
          locale: configuration.locale,
          aggregator: configuration.aggregator,
          shipping_address: configuration.shipping_address,
          recaptcha_answer: recaptchaAnswer,
        });

        const testSession = headers.location as string;
        if (!cancelled) await poll(testSession);
      } catch (error) {
        if (cancelled) return;
        const status =
          typeof error === 'object' && error !== null && 'response' in error
            ? (error as { response?: { status?: number } }).response?.status
            : undefined;
        callbacks.current.onError(status);
      }
    };

    void start();

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
    };
    // Started once per mount, exactly like the class component it replaces.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!testStatus || testStatus.running) {
    return (
      <div className="flex flex-col gap-4">
        <Typography variant="h3">Test running</Typography>
        <Loader2 className="text-on-surface-muted size-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="h3">Test results</Typography>
      <div className="flex flex-col gap-2">
        {testStatus.tests.map((test) => (
          <TestReport key={test.name} test={test} />
        ))}
      </div>
      <Surface color="weak">
        <Typography variant="p">
          Done in {testStatus.run_time} seconds
        </Typography>
      </Surface>
    </div>
  );
}
